import React, { useState } from "react";
import { useLanguage, T } from "@/lib/i18n";
// backend API removed — submissions go to Google Forms only
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";

const initialForm = { name: "", phone: "", profession: "", office: "", district: "", message: "" };

// Google Form submission endpoint and field mappings
const GOOGLE_FORM_ID = "1P0uoJwUHT5mt8IflYHBbOfWmOHBlzrUXBE7TZds1f7w";
const GOOGLE_FORM_RESPONSE_URL = `https://docs.google.com/forms/d/e/1FAIpQLSdr3nDFeZVf0mmmdpOjfrLiPhAQ1_dTuPDa0ERK0WGjshzwSQ/formResponse`;
const GOOGLE_FORM_FIELDS = {
  name: "entry.80434159",
  phone: "entry.1533920006",
  profession: "entry.997684097",
  office: "entry.1984229179",
  district: "entry.1626649139",
  message: "entry.1381433970",
};

// Validate Indian phone number from Kolkata
const validateIndianPhoneNumber = (phoneNumber) => {
  // Remove spaces, dashes, and + sign
  const cleanPhone = phoneNumber.replace(/[\s\-+()]/g, "");
  
  // Must be exactly 10 digits
  if (!/^\d{10}$/.test(cleanPhone)) {
    return false;
  }
  
  const firstDigit = cleanPhone.charAt(0);
  
  // Must start with 6, 7, 8, or 9 (valid Indian mobile)
  if (!/^[6-9]/.test(firstDigit)) {
    return false;
  }
  
  
  return true;
};

export default function JoinSection() {
  const { lang } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(lang === "bn" ? "নাম ও ফোন আবশ্যক" : "Name and phone are required");
      return;
    }

    // Validate phone number
    if (!validateIndianPhoneNumber(form.phone)) {
      const errorMsg = lang === "bn" 
        ? "অনুগ্রহ করে একটি বৈধ ভারতীয় ফোন নম্বর লিখুন"
        : "Please enter a valid Indian phone number from Kolkata";
      toast.error(errorMsg);
      return;
    }
    setSubmitting(true);
    try {
      // Submit to Google Form
      const formData = new FormData();
      formData.append(GOOGLE_FORM_FIELDS.name, form.name);
      formData.append(GOOGLE_FORM_FIELDS.phone, form.phone);
      formData.append(GOOGLE_FORM_FIELDS.profession, form.profession);
      formData.append(GOOGLE_FORM_FIELDS.office, form.office);
      formData.append(GOOGLE_FORM_FIELDS.district, form.district);
      formData.append(GOOGLE_FORM_FIELDS.message, form.message);

      await fetch(GOOGLE_FORM_RESPONSE_URL, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });

      // Also submit to backend API for database storage
      // await api.post("/volunteers", form);
      
      toast.success(T.join.success[lang]);
      setForm(initialForm);
    } catch (err) {
      // API removed — show generic error
      toast.error(T.join.fail[lang]);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, phone: value });
    
    // Clear error when user starts typing
    if (phoneError && value.length > 0) {
      setPhoneError("");
    }
    
    // Validate as user types
    if (value.length === 10 && !validateIndianPhoneNumber(value)) {
      setPhoneError(lang === "bn" 
        ? "কোলকাতা থেকে একটি বৈধ নম্বর প্রয়োজন"
        : "Please enter a valid Kolkata phone number");
    } else if (value.length < 10) {
      setPhoneError("");
    }
  };

  const fields = [
    { id: "name", k: "name", type: "text", required: true },
    { id: "phone", k: "phone", type: "tel", required: true },
    { id: "profession", k: "profession", type: "text" , required: true},
    { id: "office", k: "office", type: "numbers", required: true },
    { id: "district", k: "district", type: "text", required: true },
  ];

  return (
    <section id="join" data-testid="join-section" className="bg-white border-t border-sjm-line">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-sjm-red">{T.join.eyebrow[lang]}</div>
            <h2 className={`mt-3 font-display font-extrabold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-sjm-ink leading-tight ${lang === "bn" ? "font-bn" : ""}`}>
              {T.join.title[lang]}
            </h2>
            <p className={`mt-5 text-sjm-muted text-lg ${lang === "bn" ? "font-bn" : ""}`}>{T.join.subtitle[lang]}</p>

            <div className="mt-10 space-y-4">
              <div className="text-xs uppercase tracking-[0.2em] text-sjm-muted">{T.contact.eyebrow[lang]}</div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-sjm-red mt-0.5 shrink-0" />
                <span className={`text-sjm-ink ${lang === "bn" ? "font-bn" : ""}`}>{T.contact.address[lang]}</span>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-sjm-red mt-0.5 shrink-0" />
                <span className="text-sjm-ink">sjm4bengal@gmail.com</span>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-sjm-red mt-0.5 shrink-0" />
                <span className="text-sjm-ink">9332519733 / 9432364131</span>
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="join-form"
            className="lg:col-span-7 border border-sjm-line p-6 sm:p-10 bg-white"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {fields.map((f) => (
                <div key={f.id} className={f.id === "name" || f.id === "message" ? "sm:col-span-2" : ""}>
                  <label htmlFor={f.id} className="block text-xs font-bold uppercase tracking-wider text-sjm-ink mb-2">
                    {T.join[f.k][lang]} {f.required && <span className="text-sjm-red">*</span>}
                  </label>
                  <Input
                    id={f.id}
                    name={f.id}
                    type={f.type}
                    required={f.required}
                    data-testid={`join-input-${f.id}`}
                    placeholder={
                      f.id === "office"
                        ? lang === "bn"
                          ? "আপনার অফিসের নাম"
                          : "Name of your office"
                        : undefined
                    }
                    value={form[f.id]}
                    onChange={f.id === "phone" ? handlePhoneChange : (e) => setForm({ ...form, [f.id]: e.target.value })}
                    className={`rounded-none h-11 focus-visible:ring-1 focus-visible:ring-sjm-red focus-visible:border-sjm-red ${
                      phoneError && f.id === "phone" 
                        ? "border-sjm-red/50" 
                        : "border-sjm-ink/20"
                    }`}
                  />
                  {phoneError && f.id === "phone" && (
                    <p className="mt-1 text-xs text-sjm-red font-medium">{phoneError}</p>
                  )}
                </div>
              ))}
              <div className="sm:col-span-2">
                <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-sjm-ink mb-2">
                  {T.join.message[lang]}
                </label>
                <Textarea
                  id="message"
                  data-testid="join-input-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="rounded-none border-sjm-ink/20 focus-visible:ring-sjm-red focus-visible:ring-1 focus-visible:border-sjm-red"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="join-submit-btn"
              className="mt-7 inline-flex items-center gap-2 px-7 py-4 bg-sjm-red text-white font-bold uppercase tracking-wider text-sm hover:bg-[#D62828] disabled:opacity-60 transition-all"
            >
              {submitting ? "..." : T.join.submit[lang]}
            </button>
          </form>
        </div>
      </div>

      {/* Contact anchor */}
      <div id="contact" className="-mt-1" aria-hidden />
    </section>
  );
}
